import * as React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { dayjs } from "Config/dayjs";
import { FormatData } from "utils/FormatData";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "Components/General/Button";
import { ModalConsumer } from "Components/General/Modal/ModalContext";
import { ScrollableTable, TableColumnProps } from "Components/ScrollableTable/ScrollableTable";
import { Row, Tooltip } from "antd";
import { getFiltersForTable } from "utils/libHelpers/getFiltersForTable";
import { FileTag } from "./FileTag";
import { CollectionFolderFile } from "./interfaces";
import { DocumentsActionsMenu } from "../Components/DocumentsActionsMenu";
import classnames from "classnames";
import { DirectDownload } from "../Components/DirectDownload";
import { fileDetailsStore } from "Components/FileDetails/FileDetailsStore";
import { DocumentsTableItem } from "../Stores/DocumentsTableStore";
import { collectionFolderStore } from "./CollectionFolderStore";

interface CollectionFilesTableProps extends WithTranslation {
  data: CollectionFolderFile[];
  loading: boolean;
  onDelete: (id: number) => void;
  onClearAll: () => void;
  toggleFilesPicker: () => void;
}

export const CollectionFilesTable = withTranslation(I18N_NS.PROJECT)(
  ({ t, data, onClearAll, onDelete, toggleFilesPicker, loading }: CollectionFilesTableProps) => {
    const [selectedFileIds, setSelectFileIds] = React.useState<number[]>([]);

    const resetSelection = () => setSelectFileIds([]);

    const columns: TableColumnProps<CollectionFolderFile>[] = [
      {
        dataKey: "name",
        label: t("Filename"),
        width: 330,
        disableSort: false,
      },
      {
        dataKey: "tag",
        label: "Label",
        width: 170,
        cellRenderer: ({ cellData }) => (cellData ? <FileTag label={cellData} /> : null),
        disableSort: false,
        filters: [
          {
            text: "Uploaded",
            value: "UPLOADED",
          },
          {
            text: "Print job",
            value: "PRINT JOB",
          },
          {
            text: "Watermark req.",
            value: "RWM",
          },
          {
            text: t("Marked as deleted"),
            value: "DELETED",
          },
        ],
        filterPredicate: (filter, value) => filter.indexOf(value) !== -1,
      },
      {
        dataKey: "index",
        label: t("Index"),
        width: 110,
        disableSort: false,
        cellRenderer: ({ cellData }) => cellData || "-",
      },
      {
        dataKey: "type",
        label: t("Type"),
        width: 60,
        flexShrink: 0,
        cellRenderer: ({ cellData }) => cellData || "-",
        filters: getFiltersForTable(data, "type"),
        filterPredicate: (filter, value) => filter.indexOf(value) !== -1,
      },
      {
        dataKey: "size",
        label: t("Size"),
        width: 110,
        disableSort: false,
        cellRenderer: ({ cellData }) => (cellData ? FormatData.formatBytes(cellData, 1) : "-"),
      },
      {
        dataKey: "created",
        label: t("Last change"),
        width: 180,
        disableSort: false,
        initialSortOrder: "DESC",
        cellRenderer: ({ cellData }) => (cellData ? dayjs(cellData).format("lll") : "-"),
      },
      // {
      //   dataKey: 'ownerId',
      //   label: t('Owner'),
      //   width: .1,
      //   cellRenderer: ({ cellData }) => <UserName id={cellData} />,
      //   disableSort: true,
      // },
      {
        dataKey: "id",
        width: 80,
        style: { textAlign: "center" },
        cellRenderer: ({ rowData, cellData }) => (
          <Row justify="end" align="middle">
            {(rowData.fileActions?.download || rowData.fileActions?.downloadWithWatermark) && (
              <DirectDownload module="DOCUMENTS" fileId={cellData} />
            )}
            <Tooltip title={t("Remove from collection folder")}>
              <span
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDelete(cellData);
                }}
                style={{ cursor: "pointer" }}
              >
                <FontAwesomeIcon icon={["far", "trash-alt"]} fixedWidth={true} />
              </span>
            </Tooltip>
          </Row>
        ),
        disableSort: true,
      },
    ];
    const getRowClassName = ({ state, unread, deleted }: DocumentsTableItem) => {
      if (state) {
        return classnames({ unread: unread, disabledCell: state && deleted });
      }
      return undefined;
    };
    return (
      <div className="scrollable-table-wrapper collection-folder-table collection-table">
        <Row justify="space-between" className="mb-2">
          <div>
            <ModalConsumer>
              {({ showModal }) => (
                <DocumentsActionsMenu
                  showOnlyDropdown={true}
                  isCollectionFolder
                  resetSelection={resetSelection}
                  showModal={showModal}
                  files={data.filter((file) => selectedFileIds.includes(file.id)) as API_FileDataEntity[]}
                  onAfterActionFinished={collectionFolderStore.fetch}
                />
              )}
            </ModalConsumer>
          </div>
          <div>
            <Button grey={true} style={{ marginRight: 10 }} faIcon={"trash-alt"} onClick={onClearAll}>
              {t("Clear collection folder")}
            </Button>
            <Button type="primary" faIcon={"plus"} onClick={toggleFilesPicker}>
              {t("Add files")}
            </Button>
          </div>
        </Row>
        <ScrollableTable<CollectionFolderFile>
          loading={loading}
          rowKey="id"
          data={data}
          columns={columns}
          defaultSortBy={"tag"}
          stretchHeight={true}
          rowClassName={getRowClassName}
          selectedRowKeys={selectedFileIds}
          onSelectRows={({ selectedRows }) => setSelectFileIds(selectedRows.map((file) => file.id))}
          onRowClick={({ rowData }) => {
            if (rowData.state !== "DELETED") {
              fileDetailsStore.initModal(rowData.parentId, rowData.id, {
                from: "collection_folder",
              });
            }
          }}
          draggableRows={true}
          resizeableHeaders={"DOCUMENTS_COLLECTION"}
          ignoreResizeableHeaders={["id", "created"]}
        />
      </div>
    );
  }
);
